import { action, computed, observable, runInAction, makeObservable } from 'mobx'
import { searchWriters } from '@App/api/routes/search'
import { isEmptyObject } from '@App/helpers/check-is-empty-object'
import RootStore from '@App/stores/root-store'
import { PaginationBody } from '@Entities/pagination'
import { WriterSearch } from '@App/stores/search/models/writer-search'
import { WriterSearchResults } from '@Entities/work/writer-search-results'
import { SearchHighlights } from '@Entities/work/search-highlights'
import { WritersSearchModel } from '@Entities/work/writers-search-model'

export default class WriterSearchStore {
    private rootStore: RootStore

    constructor(rootStore: RootStore) {
        makeObservable<WriterSearchStore, '_writerSearch' | '_writersSearchModel' | '_isSearching'>(this, {
            _writerSearch: observable,
            _writersSearchModel: observable,
            _isSearching: observable,
            isThereSearchResults: computed,
            setWriterSearch: action,
            searchWriters: action,
            clearStore: action,
        })

        this.rootStore = rootStore
    }

    private _writerSearch: WriterSearch

    private _writersSearchModel: WritersSearchModel | undefined = {
        count: 0,
        results: [],
    }

    private _isSearching = false

    get writersSearchModel(): WritersSearchModel | undefined {
        return this._writersSearchModel
    }

    get isSearching(): boolean {
        return this._isSearching
    }

    get isThereSearchResults(): boolean {
        return !!this._writersSearchModel && this._writersSearchModel.count > 0
    }

    public setWriterSearch = (writerSearch: WriterSearch) => {
        this._writerSearch = writerSearch
    }

    public searchWriters = async (pagination: PaginationBody = { page: 1, limit: 5 }) => {
        this._isSearching = true
        if (this._writerSearch && !isEmptyObject(this._writerSearch)) {
            const writersSearchModel = await this.searchWritersApiCall(pagination, this._writerSearch)
            runInAction(() => {
                this._writersSearchModel = writersSearchModel
                this._isSearching = false
            })
        } else {
            runInAction(() => {
                this._writersSearchModel = {
                    count: 0,
                    results: [],
                }
                this._isSearching = false
            })
        }
    }

    private searchWritersApiCall = async (pagination: PaginationBody, search: WriterSearch) => {
        const writersSearchModel = await searchWriters(pagination, search)
        return {
            results: writersSearchModel.data.content.map((writer): WriterSearchResults => ({
                entity: writer,
                highlights: {} as SearchHighlights,
            })),
            count: writersSearchModel.data.totalElements,
        }
    }

    public clearStore = () => {
        this._writersSearchModel = {
            count: 0,
            results: [],
        }
        this._writerSearch = {
            fullName: '',
            ipiNameNumber: '',
        }
        this._isSearching = false
    }
}
